import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Activity, Users, MapPin, DollarSign, Clock, TrendingUp, Navigation, Zap } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Custom vehicle icon
const vehicleIcon = (color = '#06b6d4') => L.divIcon({
  className: 'custom-vehicle-icon',
  html: `<div style="
    width: 24px;
    height: 24px;
    background: ${color};
    border: 2px solid white;
    border-radius: 50%;
    box-shadow: 0 0 10px ${color}, 0 0 20px ${color}50;
    display: flex;
    align-items: center;
    justify-content: center;
  ">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
      <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
    </svg>
  </div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const pickupIcon = L.divIcon({
  className: 'custom-pickup-icon',
  html: `<div style="
    width: 16px;
    height: 16px;
    background: #10b981;
    border: 2px solid white;
    border-radius: 50%;
    box-shadow: 0 0 10px #10b981;
  "></div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

const dropoffIcon = L.divIcon({
  className: 'custom-dropoff-icon',
  html: `<div style="
    width: 16px;
    height: 16px;
    background: #7c3aed;
    border: 2px solid white;
    border-radius: 50%;
    box-shadow: 0 0 10px #7c3aed;
  "></div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

// Animated vehicle component
const AnimatedVehicle = ({ route, duration = 10000, color }) => {
  const [position, setPosition] = useState(route[0]);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex(prev => {
        const next = (prev + 1) % route.length;
        setPosition(route[next]);
        return next;
      });
    }, duration / route.length);
    
    return () => clearInterval(interval);
  }, [route, duration]);
  
  return <Marker position={position} icon={vehicleIcon(color)} />;
};

// Sample active trips with realistic routes
const activeTrips = [
  {
    id: 1,
    driver: 'M. Johnson',
    status: 'in-progress',
    pickup: { name: 'Metro Hospital', coords: [33.749, -84.388] },
    dropoff: { name: 'Oak Street Clinic', coords: [33.785, -84.412] },
    route: [
      [33.749, -84.388], [33.755, -84.392], [33.762, -84.398],
      [33.770, -84.405], [33.778, -84.410], [33.785, -84.412]
    ],
    eta: '8 min',
    color: '#06b6d4',
    progress: 65,
  },
  {
    id: 2,
    driver: 'S. Williams',
    status: 'picking-up',
    pickup: { name: 'Downtown Center', coords: [33.755, -84.395] },
    dropoff: { name: 'Sunrise Dialysis', coords: [33.800, -84.350] },
    route: [
      [33.755, -84.395], [33.765, -84.385], [33.775, -84.370],
      [33.788, -84.358], [33.800, -84.350]
    ],
    eta: '3 min',
    color: '#10b981',
    progress: 25,
  },
  {
    id: 3,
    driver: 'R. Davis',
    status: 'in-progress',
    pickup: { name: 'Valley Medical', coords: [33.720, -84.420] },
    dropoff: { name: 'Pine Ave Residence', coords: [33.680, -84.480] },
    route: [
      [33.720, -84.420], [33.710, -84.435], [33.700, -84.450],
      [33.690, -84.465], [33.680, -84.480]
    ],
    eta: '12 min',
    color: '#7c3aed',
    progress: 40,
  },
  {
    id: 4,
    driver: 'J. Smith',
    status: 'assigned',
    pickup: { name: 'Northside Pharmacy', coords: [33.810, -84.360] },
    dropoff: { name: 'Central Hospital', coords: [33.760, -84.390] },
    route: [
      [33.810, -84.360], [33.795, -84.368], [33.780, -84.378],
      [33.770, -84.385], [33.760, -84.390]
    ],
    eta: '5 min',
    color: '#f59e0b',
    progress: 10,
  },
];

export const LivePlatformSection = () => {
  const [stats, setStats] = useState({
    activeTrips: 1523,
    driversOnline: 892,
    citiesActive: 247,
    revenueToday: 302400,
    avgResponseTime: 2.8,
    satisfactionRate: 98.4
  });
  
  const [selectedTrip, setSelectedTrip] = useState(activeTrips[0]);

  useEffect(() => {
    const interval = setInterval(() => {
      setStats(prev => ({
        activeTrips: prev.activeTrips + Math.floor(Math.random() * 10) - 4,
        driversOnline: prev.driversOnline + Math.floor(Math.random() * 6) - 3,
        citiesActive: 247,
        revenueToday: prev.revenueToday + Math.floor(Math.random() * 500),
        avgResponseTime: parseFloat(Math.max(1.5, Math.min(4.5, parseFloat(prev.avgResponseTime) + (Math.random() - 0.5) * 0.3)).toFixed(1)),
        satisfactionRate: parseFloat(Math.max(96, Math.min(99.5, parseFloat(prev.satisfactionRate) + (Math.random() - 0.5) * 0.2)).toFixed(1))
      }));
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section 
      id="live-platform" 
      className="relative py-24 lg:py-32 overflow-hidden"
      data-testid="live-platform-section"
    >
      <div className="absolute inset-0 neural-bg opacity-20" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-neon-green/10 border border-neon-green/30 mb-6">
            <span className="w-2 h-2 rounded-full bg-neon-green mr-2 animate-pulse" />
            <span className="text-sm font-mono text-neon-green uppercase tracking-wider">Live Platform</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-6">
            Real-Time Operations
          </h2>
          <p className="max-w-2xl mx-auto text-lg text-slate-400">
            Watch the MedexOVA AI optimize routes and coordinate trips in real-time.
          </p>
        </motion.div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Activity} label="Active Trips" value={stats.activeTrips.toLocaleString()} trend="+12%" color="cyan" />
          <StatCard icon={Users} label="Drivers Online" value={stats.driversOnline.toLocaleString()} trend="+8%" color="purple" />
          <StatCard icon={MapPin} label="Cities Active" value={stats.citiesActive} trend="+3" color="blue" />
          <StatCard icon={DollarSign} label="Revenue Today" value={`$${(stats.revenueToday / 1000).toFixed(0)}K`} trend="+15%" color="green" />
        </div>

        {/* Main Dashboard */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Live Map */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="glass-card overflow-hidden h-[500px] relative"
              data-testid="live-map"
            >
              <div className="absolute top-4 left-4 z-[1000] glass px-4 py-2 rounded-lg">
                <h3 className="text-lg font-heading font-semibold text-white">AI Route Optimization</h3>
                <p className="text-xs text-slate-400">Real-time trip tracking & coordination</p>
              </div>
              
              {/* AI Processing Indicator */}
              <div className="absolute top-4 right-4 z-[1000] glass px-3 py-2 rounded-lg flex items-center space-x-2">
                <Zap className="w-4 h-4 text-neon-cyan animate-pulse" />
                <span className="text-xs text-neon-cyan font-mono">AI OPTIMIZING</span>
              </div>
              
              <MapContainer
                center={[33.755, -84.390]}
                zoom={11}
                style={{ height: '100%', width: '100%' }}
                zoomControl={false}
              >
                <TileLayer
                  url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                />
                
                {/* Render all trip routes */}
                {activeTrips.map((trip) => (
                  <React.Fragment key={trip.id}>
                    {/* Route line with glow effect */}
                    <Polyline
                      positions={trip.route}
                      pathOptions={{
                        color: trip.color,
                        weight: 4,
                        opacity: 0.8,
                        dashArray: trip.status === 'assigned' ? '10, 10' : null,
                      }}
                    />
                    <Polyline
                      positions={trip.route}
                      pathOptions={{
                        color: trip.color,
                        weight: 8,
                        opacity: 0.3,
                      }}
                    />
                    
                    {/* Pickup marker */}
                    <Marker position={trip.pickup.coords} icon={pickupIcon}>
                      <Popup className="custom-popup">
                        <div className="text-sm">
                          <strong className="text-green-500">Pickup</strong>
                          <p>{trip.pickup.name}</p>
                        </div>
                      </Popup>
                    </Marker>
                    
                    {/* Dropoff marker */}
                    <Marker position={trip.dropoff.coords} icon={dropoffIcon}>
                      <Popup className="custom-popup">
                        <div className="text-sm">
                          <strong className="text-purple-500">Dropoff</strong>
                          <p>{trip.dropoff.name}</p>
                        </div>
                      </Popup>
                    </Marker>
                    
                    {/* Animated vehicle */}
                    <AnimatedVehicle route={trip.route} duration={8000 + trip.id * 2000} color={trip.color} />
                  </React.Fragment>
                ))}
              </MapContainer>
            </motion.div>
          </div>

          {/* Right Panel */}
          <div className="space-y-6">
            {/* Performance Metrics */}
            <div className="glass-card p-6">
              <h3 className="text-lg font-heading font-semibold text-white mb-4 flex items-center">
                <Zap className="w-5 h-5 text-neon-cyan mr-2" />
                AI Performance
              </h3>
              <div className="space-y-4">
                <MetricBar label="Avg Response Time" value={`${stats.avgResponseTime}s`} percentage={100 - (stats.avgResponseTime / 5) * 100} color="cyan" />
                <MetricBar label="Satisfaction Rate" value={`${stats.satisfactionRate}%`} percentage={stats.satisfactionRate} color="green" />
                <MetricBar label="Route Efficiency" value="94%" percentage={94} color="purple" />
                <MetricBar label="Driver Utilization" value="87%" percentage={87} color="blue" />
              </div>
            </div>

            {/* Live Trips Feed */}
            <div className="glass-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-heading font-semibold text-white">Active Trips</h3>
                <span className="text-xs font-mono text-neon-green flex items-center">
                  <span className="w-1.5 h-1.5 rounded-full bg-neon-green mr-1 animate-pulse" />
                  LIVE
                </span>
              </div>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {activeTrips.map((trip) => (
                  <motion.div
                    key={trip.id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className={`p-3 rounded-lg border transition-all cursor-pointer ${
                      selectedTrip?.id === trip.id 
                        ? 'bg-white/10 border-neon-cyan/50' 
                        : 'bg-black/30 border-white/5 hover:border-white/20'
                    }`}
                    onClick={() => setSelectedTrip(trip)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs font-mono px-2 py-0.5 rounded-full`} style={{ backgroundColor: `${trip.color}20`, color: trip.color }}>
                        {trip.status.replace('-', ' ')}
                      </span>
                      <span className="text-xs text-slate-500">{trip.eta}</span>
                    </div>
                    <div className="flex items-center space-x-2 mb-1">
                      <div className="w-2 h-2 rounded-full bg-neon-green" />
                      <p className="text-sm text-slate-300 truncate">{trip.pickup.name}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 rounded-full bg-neon-purple" />
                      <p className="text-sm text-slate-400 truncate">{trip.dropoff.name}</p>
                    </div>
                    {/* Progress bar */}
                    <div className="mt-2 h-1 bg-slate-800 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full rounded-full"
                        style={{ backgroundColor: trip.color, width: `${trip.progress}%` }}
                        animate={{ width: `${trip.progress + 5}%` }}
                        transition={{ duration: 2, repeat: Infinity, repeatType: 'reverse' }}
                      />
                    </div>
                    <p className="text-xs text-slate-600 mt-1">{trip.driver}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const StatCard = ({ icon: Icon, label, value, trend, color }) => {
  const colors = {
    cyan: 'text-neon-cyan border-neon-cyan/30',
    purple: 'text-neon-purple border-neon-purple/30',
    blue: 'text-neon-blue border-neon-blue/30',
    green: 'text-neon-green border-neon-green/30',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={`glass-card p-4 border ${colors[color].split(' ')[1]}`}
    >
      <Icon className={`w-5 h-5 ${colors[color].split(' ')[0]} mb-2`} />
      <div className="text-2xl font-heading font-bold text-white">{value}</div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-500">{label}</span>
        <span className="text-xs text-neon-green flex items-center">
          <TrendingUp className="w-3 h-3 mr-1" />
          {trend}
        </span>
      </div>
    </motion.div>
  );
};

const MetricBar = ({ label, value, percentage, color }) => {
  const colors = {
    cyan: 'bg-neon-cyan',
    purple: 'bg-neon-purple',
    blue: 'bg-neon-blue',
    green: 'bg-neon-green',
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-slate-400">{label}</span>
        <span className="text-sm font-mono text-white">{value}</span>
      </div>
      <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          whileInView={{ width: `${percentage}%` }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: "easeOut" }}
          className={`h-full ${colors[color]} rounded-full`}
        />
      </div>
    </div>
  );
};

export default LivePlatformSection;
